import React, { useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Play, Pause, Rss } from "lucide-react";
import { PublicShell, Loading } from "@/components/shared";
import { NewsletterInline } from "@/components/Newsletter";
import { AudioPlayer } from "@/components/podcast/AudioPlayer";
import { ShowCover, SHOW_NAME, SHOW_HOST, fmtDate, fmtMinutes, PODCAST_PROMISE, PODCAST_DONE } from "@/components/podcast/common";
import { useHead } from "@/lib/useHead";
import api from "@/lib/api";

function PlatformButtons({ show }) {
  const links = [["apple", "Apple Podcasts", show.apple_url], ["spotify", "Spotify", show.spotify_url], ["rss", "RSS", show.feed_url]].filter(([, , u]) => u);
  if (!links.length) return null;
  return (
    <div className="mt-5 flex flex-wrap gap-2" data-testid="podcast-platforms">
      {links.map(([k, label, url]) => (
        <a key={k} href={url} target="_blank" rel="noopener noreferrer" data-testid={`podcast-link-${k}`}
          className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-background px-3.5 py-2 text-sm font-semibold text-ink transition-colors hover:border-accent hover:text-accent">
          {k === "rss" && <Rss className="h-3.5 w-3.5" />}{label}
        </a>
      ))}
    </div>
  );
}

function ShowHeader({ show }) {
  return (
    <header className="flex flex-col gap-6 sm:flex-row sm:items-start" data-testid="podcast-header">
      <ShowCover src={show.image_url} className="w-40 flex-shrink-0 sm:w-44" />
      <div className="min-w-0">
        <p className="kicker text-xs text-accent">Podcast</p>
        <h1 className="mt-2 font-heading text-[clamp(2rem,6vw,2.75rem)] font-extrabold leading-tight text-ink" data-testid="podcast-title">{SHOW_NAME}</h1>
        <p className="mt-1 text-sm font-semibold text-muted" data-testid="podcast-host">Hosted by {SHOW_HOST}</p>
        {show.description && <p className="mt-4 text-[15px] leading-relaxed text-ink" data-testid="podcast-description">{show.description}</p>}
        <PlatformButtons show={show} />
      </div>
    </header>
  );
}

function EpisodeRow({ ep }) {
  const [open, setOpen] = useState(false);
  const [playing, setPlaying] = useState(false);
  const audio = useRef(null);
  const press = () => {
    if (!open) return setOpen(true);
    const a = audio.current;
    if (a) { if (a.paused) a.play().catch(() => {}); else a.pause(); }
  };
  return (
    <li className="py-6" data-testid={`episode-row-${ep.slug}`}>
      <div className="flex items-start gap-4">
        <div className="min-w-0 flex-1">
          <p className="kicker text-[11px] text-accent">
            {ep.episode_number ? `Episode ${ep.episode_number}` : "Episode"}<span className="text-muted"> · {fmtDate(ep.published_at)}{ep.duration_seconds ? ` · ${fmtMinutes(ep.duration_seconds)}` : ""}</span>
          </p>
          <Link to={`/podcast/${ep.slug}`} className="mt-1.5 block font-heading text-lg font-bold leading-snug text-ink hover:text-accent" data-testid={`episode-link-${ep.slug}`}>{ep.title}</Link>
          <p className="mt-1.5 line-clamp-2 text-sm text-muted">{(ep.description_text || "").slice(0, 200)}</p>
        </div>
        <button onClick={press} aria-label={playing ? `Pause ${ep.title}` : `Play ${ep.title}`} data-testid={`episode-play-${ep.slug}`}
          className="mt-1 flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-accent text-accent-foreground transition-transform hover:scale-105">
          {playing ? <Pause className="h-5 w-5" fill="currentColor" /> : <Play className="ml-0.5 h-5 w-5" fill="currentColor" />}
        </button>
      </div>
      {open && (
        <div className="mt-4">
          <AudioPlayer src={ep.audio_url} durationHint={ep.duration_seconds} startOnMount apiRef={audio} onPlayingChange={setPlaying} testid={`player-${ep.slug}`} />
        </div>
      )}
    </li>
  );
}

export function PodcastSubscribe() {
  return (
    <div data-testid="podcast-subscribe">
      <NewsletterInline source="podcast" promise={PODCAST_PROMISE} doneText={PODCAST_DONE} finePrint="Free. Unsubscribe in one click." />
    </div>
  );
}

export default function Podcast() {
  const [params] = useSearchParams();
  const page = Math.max(1, parseInt(params.get("page") || "1", 10) || 1);
  const { data, isLoading } = useQuery({ queryKey: ["podcast", page], queryFn: async () => (await api.get(`/podcast?page=${page}`)).data });
  const show = data?.show || {};
  const url = `${window.location.origin}/podcast`;
  useHead({
    title: `${SHOW_NAME} — a podcast hosted by ${SHOW_HOST}`,
    description: show.description || `${SHOW_NAME}, a podcast about the Indian jewellery trade, hosted by ${SHOW_HOST}.`,
    image: show.image_url, url,
    jsonLd: data && { "@context": "https://schema.org", "@type": "PodcastSeries", name: SHOW_NAME, url,
      description: show.description || undefined, image: show.image_url || undefined, webFeed: show.feed_url || undefined,
      author: { "@type": "Person", name: SHOW_HOST } },
  });
  const eps = data?.episodes || [];

  return (
    <PublicShell>
      <div className="podcast-scope bg-background">
        <div className="mx-auto max-w-[760px] px-5 py-14" data-testid="podcast-page">
          {isLoading ? <Loading /> : (
            <>
              <ShowHeader show={show} />
              <div className="mt-10"><PodcastSubscribe /></div>
              {data.total === 0 ? (
                <div className="mt-6 rounded-[10px] border border-border bg-card p-6" data-testid="podcast-launch-card">
                  <p className="font-heading text-lg font-bold text-ink">Episode one is being recorded</p>
                </div>
              ) : (
                <>
                  <ul className="mt-10 divide-y divide-border border-y border-border" data-testid="podcast-episode-list">
                    {eps.map((ep) => <EpisodeRow key={ep.guid} ep={ep} />)}
                  </ul>
                  <div className="mt-6 flex justify-between text-sm font-semibold">
                    {page > 1 ? <Link to={page === 2 ? "/podcast" : `/podcast?page=${page - 1}`} className="text-accent hover:underline" data-testid="podcast-newer">Newer episodes</Link> : <span />}
                    {data.has_older && <Link to={`/podcast?page=${page + 1}`} className="text-accent hover:underline" data-testid="podcast-older">Older episodes</Link>}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </PublicShell>
  );
}
